import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './DevilFruitQuiz.css';
import gameConfig from '../config/devilFruitGameConfig';
import { db } from "../firebase";
import { collection, addDoc } from "firebase/firestore";

// Leaderboard imports
import { getCast, fullCast } from '../data/characterCast';
import { saveScore } from '../services/leaderboardService';
import { recordPlay } from '../services/playHistory';
import { useUsername } from '../hooks/useUsername';
import UsernameModal from '../components/UsernameModal/UsernameModal';
import QuizPageText from '../components/QuizPageText/QuizPageText';
import { devilFruitQuizText } from '../components/QuizPageText/quizPageText.data';

// Analytics
import { trackQuizStart, trackQuizComplete } from '../services/analyticsService';
import useGameFullscreen from '../hooks/useGameFullscreen';

const DevilFruitQuiz = ({ animeIds }) => {
  const navigate = useNavigate();
  const { username, avatar, hasUsername, register } = useUsername();
  const [characters, setCharacters] = useState([]);
  const [animeData, setAnimeData] = useState({});
  const [questionIndex, setQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [isAnswered, setIsAnswered] = useState(false);
  const [choices, setChoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [incorrectAnswers, setIncorrectAnswers] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  useGameFullscreen(gameStarted && !gameOver);
  const [passesUsed, setPassesUsed] = useState(0);
  const [questionsLeft, setQuestionsLeft] = useState(gameConfig.maxQuestions);
  const [answeredQuestions, setAnsweredQuestions] = useState(0);
  const [introScreen, setIntroScreen] = useState(true);
  const [timeLeft, setTimeLeft] = useState(gameConfig.timePerQuestion);

  // Leaderboard states
  const [gameStartTime, setGameStartTime] = useState(null);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [pendingLeaderboardSave, setPendingLeaderboardSave] = useState(null);

  const backgroundMusic = useRef(null);

  useEffect(() => {
    if (gameConfig.sounds.backgroundMusic) {
      if (backgroundMusic.current) {
        backgroundMusic.current.pause();
        backgroundMusic.current.currentTime = 0;
      }
      backgroundMusic.current = new Audio(gameConfig.sounds.backgroundMusic);
      backgroundMusic.current.loop = true;
    }
  }, []);

  useEffect(() => {
    if (timeLeft > 0 && !isAnswered && gameStarted && !gameOver) {
      const timer = setTimeout(() => setTimeLeft((prev) => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !isAnswered) {
      handleTimeout();
    }
  }, [timeLeft, isAnswered, gameStarted, gameOver]);

  const handleTimeout = () => {
    setIncorrectAnswers((prev) => prev + 1);
    setAnsweredQuestions((prev) => prev + 1);
    setQuestionsLeft((prev) => prev - 1);
    setIsAnswered(true);
    if (answeredQuestions + 1 === gameConfig.maxQuestions) {
      setGameOver(true);
    }
    // Removed auto-advance - user must click Next
  };
  const incorrectSound = new Audio(gameConfig.sounds.incorrectSound);
  // One save per round. Registering a name logs the player in, which changes
  // hasUsername/username — deps of the game-over effect below — so without this
  // the same round was saved a second time. (BountyBattle already guards this way.)
  const savedThisRound = useRef(false);

  const gameOverSound = useRef(new Audio(gameConfig.sounds.gameOverSound));
  const correctAnswerSound = new Audio(gameConfig.sounds.correctSound);

  const fetchGameData = async (animeId) => {
    try {
      // Only characters we have a Devil Fruit answer for. This used to run over
      // the Jikan response, where names arrive as "Monkey D., Luffy" while
      // devilFruits is keyed "Monkey D. Luffy" — so only 16 of the 33 fruits ever
      // matched and Luffy himself was never in the game. The bundled cast uses
      // the same spellings as the config, which brings all of them back.
      const filteredChars = getCast(fullCast.length).filter(
        (c) => gameConfig.devilFruits[c.character.name]
      );
      setCharacters(filteredChars);
      setLoading(false);

      const animeResponse = await fetch(`https://api.jikan.moe/v4/anime/${animeId}`);
      const animeDataResponse = await animeResponse.json();
      setAnimeData(animeDataResponse.data);
    } catch (error) {
      console.error('Error fetching optional anime data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (animeIds && animeIds.length > 0) {
      fetchGameData(animeIds[0]);
    }
  }, [animeIds]);

  const shuffleCharacters = (characters) => {
    let shuffled = [...characters];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const shuffledCharacters = useMemo(() => shuffleCharacters(characters), [characters]);

  useEffect(() => {
    if (shuffledCharacters && shuffledCharacters.length > 0) {
      setChoices(getDevilFruitChoices());
    }
  }, [shuffledCharacters, questionIndex]);

  const getDevilFruitChoices = () => {
    const currentChar = shuffledCharacters[questionIndex];
    if (!currentChar) return [];
    
    const correctAnswer = gameConfig.devilFruits[currentChar.character.name];
    if (!correctAnswer) return [];
    
    // Get 3 random wrong answers
    const wrongOnes = gameConfig.wrongAnswers
      .filter(w => w !== correctAnswer)
      .sort(() => 0.5 - Math.random())
      .slice(0, 3);
    
    // Mix and shuffle
    const allChoices = [correctAnswer, ...wrongOnes].sort(() => Math.random() - 0.5);
    return allChoices;
  };

  const handleAnswer = (answer) => {
    const currentChar = shuffledCharacters[questionIndex];
    const correctAnswer = gameConfig.devilFruits[currentChar.character.name];
    const isCorrect = answer === correctAnswer;
    
    const newAnsweredCount = answeredQuestions + 1;
    
    console.log('=== ANSWER CLICKED ===');
    console.log('Selected:', answer);
    console.log('Correct:', correctAnswer);
    console.log('Is Correct:', isCorrect);
    console.log('Question #:', questionIndex + 1);
    console.log('Answered so far:', newAnsweredCount);
    console.log('Max Questions:', gameConfig.maxQuestions);
    console.log('Will end game?', newAnsweredCount >= gameConfig.maxQuestions);
    
    setSelectedAnswer(answer);
    setIsAnswered(true);

    if (isCorrect) {
      correctAnswerSound.play();
      setScore(score + 1);
      setCorrectAnswers(correctAnswers + 1);
    } else {
      incorrectSound.play();
      setIncorrectAnswers(incorrectAnswers + 1);
    }

    setAnsweredQuestions(newAnsweredCount);
    setQuestionsLeft(gameConfig.maxQuestions - newAnsweredCount);

// Check if game should end
const shouldEndGame = newAnsweredCount >= gameConfig.maxQuestions || 
questionIndex + 1 >= shuffledCharacters.length;

if (shouldEndGame) {
console.log('🎯 GAME OVER!');
console.log('Answered:', newAnsweredCount, '/', gameConfig.maxQuestions);
console.log('Available characters:', shuffledCharacters.length);
setGameOver(true);
}
  };

  const handlePass = () => {
    if (passesUsed < gameConfig.maxPasses) {
      setPassesUsed(passesUsed + 1);
      nextQuestion();
    }
  };

  const nextQuestion = () => {
    console.log('📍 Next Question called. Current index:', questionIndex);
    
    if (questionIndex + 1 < shuffledCharacters.length) {
      setQuestionIndex(questionIndex + 1);
      setIsAnswered(false);
      setSelectedAnswer(null);
      setTimeLeft(gameConfig.timePerQuestion);
      console.log('✅ Moving to question:', questionIndex + 2);
    } else {
      console.log('⚠️ No more questions available!');
      setGameOver(true);
    }
  };

  const handleStartGame = () => {
    setIntroScreen(false);
    setGameStarted(true);
    setGameStartTime(Date.now()); // Track game start time for leaderboard
    trackQuizStart('devilfruit'); // Analytics tracking
    if (backgroundMusic.current) {
      backgroundMusic.current.play().catch((err) => console.error('Autoplay prevented:', err));
    }
  };

  const restartGame = () => {
    console.log('🔄 RESTARTING GAME - Full Reset');

    // Stop all sounds
    gameOverSound.current.pause();
    gameOverSound.current.currentTime = 0;
    if (backgroundMusic.current) {
      backgroundMusic.current.pause();
      backgroundMusic.current.currentTime = 0;
    }

    // Reset ALL state
    setIntroScreen(true);
    setGameStarted(false);
    setGameOver(false);
    savedThisRound.current = false;
    setScore(0);
    setCorrectAnswers(0);
    setIncorrectAnswers(0);
    setQuestionIndex(0);
    setAnsweredQuestions(0);
    setQuestionsLeft(gameConfig.maxQuestions);
    setPassesUsed(0);
    setTimeLeft(gameConfig.timePerQuestion);
    setIsAnswered(false);
    setSelectedAnswer(null);
    setChoices([]);
    setGameStartTime(null); // Reset game start time
    setShowUsernameModal(false);
    setPendingLeaderboardSave(null);

    // Fetch fresh data
    fetchGameData(animeIds[0]);
  };

  // Handle username submission from modal (with password and avatar for new registration)
  const handleUsernameSubmit = async (newUsername, password, selectedAvatar, email) => {
    const result = await register(newUsername, password, selectedAvatar, email);
    if (result.success && pendingLeaderboardSave) {
      await saveScore({ ...pendingLeaderboardSave, username: newUsername, avatar: selectedAvatar });
      setShowUsernameModal(false);
      setPendingLeaderboardSave(null);
    } else if (!result.success) {
      throw new Error(result.error);
    }
  };

  // Handle skip - don't save score to leaderboard
  const handleUsernameSkip = () => {
    setShowUsernameModal(false);
    setPendingLeaderboardSave(null); // Discard the pending score
  };

  // Game over effect - saves to Firebase AND JSONBin
  useEffect(() => {
    if (gameOver && !savedThisRound.current) {
      savedThisRound.current = true;
      // Calculate total time
      const totalTime = gameStartTime ? Math.floor((Date.now() - gameStartTime) / 1000) : 0;

      // Analytics tracking
      trackQuizComplete('devilfruit', score, 15, totalTime);

      // Save game result to Firestore (FIX: This was missing!)
      const firebaseResult = {
        score,
        correctAnswers,
        incorrectAnswers,
        date: new Date().toISOString(),
        theme: "devil_fruit_quiz",
      };

      const saveGameResult = async () => {
        try {
          const resultsRef = collection(db, "users", "guest_user", "gameResults");
          await addDoc(resultsRef, firebaseResult);
          console.log('Game result saved to Firebase for Devil Fruit quiz:', firebaseResult);
        } catch (error) {
          console.error('Error saving game result to Firebase:', error);
        }
      };

      saveGameResult();

      // Prepare leaderboard entry
      const leaderboardEntry = {
        score,
        correctAnswers,
        incorrectAnswers,
        totalTime,
        gameType: 'devilfruit',
        date: new Date().toISOString(),
      };
      recordPlay(leaderboardEntry);

      // If user has username, save immediately
      // Otherwise, show modal to collect username
      if (hasUsername && username) {
        saveScore({ ...leaderboardEntry, username, avatar }).then((result) => {
          console.log('Leaderboard save result:', result);
        });
      } else {
        setPendingLeaderboardSave(leaderboardEntry);
        setShowUsernameModal(true);
      }

      // Play game-over sound and stop background music
      gameOverSound.current.play();
      if (backgroundMusic.current) {
        backgroundMusic.current.pause();
      }
    }
  }, [gameOver, score, correctAnswers, incorrectAnswers, gameStartTime, hasUsername, username, avatar]);

  if (loading) return <p style={{ color: 'white', textAlign: 'center' }}>Loading...</p>;

  const currentChar = shuffledCharacters[questionIndex];
  const correctAnswer = currentChar ? gameConfig.devilFruits[currentChar.character.name] : '';

  return (
    <>
    <Helmet>
      <title>Devil Fruit Quiz - Name the One Piece Fruit | One Piece Trivia</title>
      <meta name="description" content="Name the Devil Fruit from One Piece - Gomu Gomu, Mera Mera, Ope Ope and more. 15 questions per round with a leaderboard." />
      <link rel="canonical" href="https://www.onepiecetrivia.com/devil-fruit-quiz" />
      <meta property="og:title" content="Devil Fruit Quiz - Name the One Piece Fruit" />
      <meta property="og:description" content="Name the Devil Fruit from One Piece. 15 questions per round with a leaderboard." />
      <meta property="og:url" content="https://www.onepiecetrivia.com/devil-fruit-quiz" />
      <meta name="twitter:title" content="Devil Fruit Quiz - Name the One Piece Fruit" />
      <meta name="twitter:description" content="Name the Devil Fruit from One Piece. 15 questions per round with a leaderboard." />
      <script type="application/ld+json">{`{
        "@context": "https://schema.org",
        "@type": "Quiz",
        "name": "One Piece Devil Fruit Quiz",
        "description": "Name the Devil Fruit from One Piece - Gomu Gomu, Mera Mera, Ope Ope and more. 15 questions per round with a leaderboard.",
        "url": "https://www.onepiecetrivia.com/devil-fruit-quiz",
        "about": { "@type": "Thing", "name": "One Piece Devil Fruits" },
        "educationalLevel": "intermediate",
        "numberOfQuestions": 15,
        "isAccessibleForFree": true,
        "inLanguage": "en"
      }`}</script>
    </Helmet>
<div className="devil-fruit-container" style={{
      backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.9)), url(${gameConfig.images.backgroundImage})`
    }}>
{introScreen ? (
        <div className="intro-screen">
          <div className="intro-content">
            <h1 className="new-onepiece-intro-title">
              <img src="/devil-fruit-quiz-and-any-related.png" alt="" className="title-icon" />
              {' '}Devil Fruit Quiz{' '}
              <img src="/devil-fruit-quiz-and-any-related.png" alt="" className="title-icon" />
            </h1>
            <p className="game-subtitle">{gameConfig.text.introSubtitle}</p>
            <div className="intro-image-container">
              <img src={gameConfig.images.introImage} alt="Game Start" className="intro-image" />
            </div>
            <button className="start-game-button" onClick={handleStartGame}>
              <span className="start-icon">🎮</span> {gameConfig.text.startButtonText}
            </button>
          </div>
        </div>
      ) : (
        <div className="devil-game-content">
          {!gameOver && (
            <>
              <div className="devil-stats">
                <span>Score: {score}</span>
                <span>Questions Left: {questionsLeft}</span>
                <span>Time: {timeLeft}s</span>
              </div>

              <div className="devil-question-area">
                <img 
                  src={currentChar?.character?.images?.jpg?.image_url} 
                  alt="Character"
                  className="devil-character-img"
                />
                <h2 style={{ color: gameConfig.theme.questionColor, fontSize: gameConfig.theme.questionFont }}>
                  {gameConfig.text.questionPrefix}
                </h2>
              </div>

              <div className="devil-choices">
                {choices.map((choice, idx) => (
                  <button
                    key={idx}
                    className={`devil-choice ${
                      isAnswered && choice === correctAnswer ? 'devil-correct' :
                      selectedAnswer === choice && choice !== correctAnswer ? 'devil-incorrect' : ''
                    }`}
                    onClick={() => handleAnswer(choice)}
                    disabled={isAnswered}
                    style={{ fontSize: gameConfig.theme.answerFont }}
                  >
                    {choice}
                  </button>
                ))}
              </div>

              {isAnswered && (
                <div className="devil-feedback">
                  {selectedAnswer === correctAnswer ? (
                    <p style={{ color: gameConfig.theme.correctColor }}>{gameConfig.text.correctFeedback}</p>
                  ) : (
                    <p style={{ color: gameConfig.theme.incorrectColor }}>
                      {gameConfig.text.incorrectFeedback} {correctAnswer}
                    </p>
                  )}
                  <button className="devil-next-btn" onClick={nextQuestion}>
                    {gameConfig.text.nextButtonText}
                  </button>
                </div>
              )}

              <button className="devil-pass-btn" onClick={handlePass} disabled={passesUsed >= gameConfig.maxPasses || isAnswered}>
                {gameConfig.text.passButtonText} ({gameConfig.maxPasses - passesUsed} left)
              </button>
            </>
          )}

          {gameOver && (
            <div className="devil-game-over">
              <h2>{gameConfig.text.gameOverTitle}</h2>
              <p>Correct: {correctAnswers}</p>
              <p>Incorrect: {incorrectAnswers}</p>
              <button className="devil-restart-btn" onClick={restartGame}>
                {gameConfig.text.restartGameOverText}
              </button>
              <button className="devil-leaderboard-btn" onClick={() => navigate('/leaderboard')}>
                View Leaderboard
              </button>
            </div>
          )}
        </div>
      )}

      {/* Username Modal for leaderboard */}
      <UsernameModal
        isOpen={showUsernameModal}
        standing={pendingLeaderboardSave && { gameType: 'devilfruit', score: pendingLeaderboardSave.score, totalTime: pendingLeaderboardSave.totalTime, max: 15 }}
        onSubmit={handleUsernameSubmit}
        onSkip={handleUsernameSkip}
        initialUsername=""
      />
    </div>
      <QuizPageText {...devilFruitQuizText} />
    </>
  );
};

export default DevilFruitQuiz;
